<script lang="ts" setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router/composables'
import { v4 as uuid } from 'uuid'
import ModuleCard from './ModuleCard.vue'
import { useElementClick } from '@/composables/tracks'
import { brand } from '@/config/brands'
import { READER_ROUTE_NAME } from '@/utils/readerRouteName'

interface Props {
  module: any
  config?: any
  cardClass?: string
  headerClass?: string
}

const props = defineProps<Props>()

const { elementClick } = useElementClick()
const router = useRouter()
const books = computed(() => {
  if (props.module?.resourceList?.records?.length > 6)
    return props.module.resourceList.records.slice(0, 6)

  return props.module?.resourceList?.records || []
})

const rankColumns = computed(() => {
  const columns: any[][] = []
  const list = books.value
  for (let i = 0; i < list.length; i += 3)
    columns.push(list.slice(i, i + 3))
  return columns
})

function gotoCollection() {
  elementClick(`seeall_${props.module.id}`)
  router.push({ name: 'ranking', params: { columnId: props.module.id }, query: { title: props.module.showName } })
}

function getMedalIcon(index: number) {
  if (index === 0)
    return `/images/${brand}/ranking/rank-badge-gold.svg`
  if (index === 1)
    return `/images/${brand}/ranking/rank-badge-silver.svg`
  if (index === 2)
    return `/images/${brand}/ranking/rank-badge-bronze.svg`
  return `/images/${brand}/ranking/rank-badge-default.svg`
}
</script>

<template>
  <ModuleCard
    :title-text="module.showName"
    :action-text-value="config?.texts?.seeAll || 'See All'"
    :card-class="cardClass"
    :header-class="headerClass"
    @action="gotoCollection"
  >
    <div class="mt-12px">
      <div class="rr-ranking-scroll overflow-x-auto no-scrollbar">
        <div class="rr-columns min-w-max pr-12px">
          <div v-for="(column, colIndex) in rankColumns" :key="`col-${colIndex}`" class="rr-column">
            <div
              v-for="(book, rowIndex) in column"
              :key="book.id"
              class="rr-ranking-item"
              @click="router.push({ name: READER_ROUTE_NAME, params: { contentId: book.id, chapterId: book.firstChapter?.id || '' } })"
            >
              <div class="rr-rank-medal" :class="`rr-rank-medal-${Math.min(colIndex * 3 + rowIndex, 2)}`">
                <img :src="getMedalIcon(colIndex * 3 + rowIndex)" width="22" height="22">
                <div class="rr-rank-num">
                  {{ colIndex * 3 + rowIndex + 1 }}
                </div>
              </div>
              <BizBookCover
                :id="uuid()"
                :src="book.contentCoverUrl || ''"
                :extra="{
                  module_name: module.showName,
                  module_id: module.id,
                  module_type: module.columnType,
                  author: book.authorInfo.author,
                  ...book,
                }"
                class="rr-cover rounded-10px flex-shrink-0"
              />
              <div class="ml-8px min-w-0 flex-1 flex flex-col justify-center self-stretch">
                <div class="text-13px lh-18px font-700 line-clamp-2 text-n-text-1">
                  {{ book.contentName }}
                </div>
                <div class="mt-5px text-11px truncate text-n-text-3">
                  {{ book.authorInfo.author }}
                </div>
                <div class="mt-5px text-11px lh-15px text-n-text-3 line-clamp-2">
                  {{ book.introduce }}
                </div>
                <BizShowTags v-if="book.tagList && book.tagList.length" :tags="book.tagList" class="mt-9px" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </ModuleCard>
</template>

<style scoped>
.rr-columns {
  display: flex;
  gap: 12px;
}

.rr-column {
  width: calc(100vw - 92px);
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.rr-ranking-item {
  display: flex;
  align-items: flex-start;
  width: 100%;
  min-width: 0;
  position: relative;
  text-decoration: none;
  padding: 3px 0;
  background: transparent !important;
  border: 0 !important;
  border-radius: 0 !important;
  box-shadow: none !important;
}

.rr-cover {
  width: calc((100vw - 68px) / 3);
  position: relative;
  z-index: 1;
}

.rr-rank-medal {
  position: absolute;
  left: 4px;
  top: 4px;
  width: 24px;
  height: 28px;
  z-index: 5;
  display: flex;
  justify-content: center;
  overflow: hidden;
  background: transparent !important;
  box-shadow: none !important;
}

.rr-rank-medal img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.rr-rank-num {
  position: absolute;
  left: 0;
  right: 0;
  top: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.18);
}

.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.no-scrollbar::-webkit-scrollbar {
  display: none;
}
</style>
